import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { ArticleAuthor, SocialProofItem, AuthorImage } from '@/types/articles';

const syncAuthorToContacts = async (
  brandId: string,
  author: { name: string; email?: string | null; phone?: string | null }
): Promise<string | null> => {
  if (!author.email) return null;
  
  // Split name into first and last name
  const nameParts = author.name.trim().split(/\s+/);
  const firstName = nameParts[0] || author.name;
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
  
  try {
    const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
      p_brand_id: brandId,
      p_email: author.email,
      p_first_name: firstName,
      p_last_name: lastName,
      p_phone: author.phone || null,
      p_module: ['CMS'],
      p_activity: ['author'],
      p_organization: null,
      p_title: null,
      p_user_id: null,
    });
    
    if (error) {
      console.error('Error syncing author to contacts:', error);
      return null;
    }
    
    return contactId;
  } catch (error) {
    console.error('Error syncing author to contacts:', error);
    return null;
  }
};

const parseAuthorData = (data: any): ArticleAuthor => ({
  ...data,
  social_proof: (data.social_proof as SocialProofItem[]) || [],
  images: (data.images as AuthorImage[]) || [],
});

export const useArticleAuthors = (brandId: string | undefined) => {
  return useQuery({
    queryKey: ['article-authors', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('os_article_authors')
        .select('*')
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .order('name', { ascending: true });
      
      if (error) throw error;
      return (data || []).map(parseAuthorData);
    },
    enabled: !!brandId
  });
};

export const useArticleExperts = (brandId: string | undefined) => {
  return useQuery({
    queryKey: ['article-experts', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('os_article_authors')
        .select('*')
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .eq('is_expert', true)
        .order('name', { ascending: true });
      
      if (error) throw error;
      return (data || []).map(parseAuthorData);
    },
    enabled: !!brandId
  });
};

export const useArticleAuthor = (authorId: string | undefined) => {
  return useQuery({
    queryKey: ['article-author', authorId],
    queryFn: async () => {
      if (!authorId) return null;
      
      const { data, error } = await supabase
        .from('os_article_authors')
        .select('*')
        .eq('id', authorId)
        .single();
      
      if (error) throw error;
      return parseAuthorData(data);
    },
    enabled: !!authorId
  });
};

export const useCreateArticleAuthor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (author: Partial<ArticleAuthor> & { brand_id: string; name: string }) => {
      const slug = author.slug || author.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, created_at, updated_at, ...insertData } = author as any;
      
      // Sync to contacts first
      const contactId = await syncAuthorToContacts(author.brand_id, {
        name: author.name,
        email: author.email,
        phone: null,
      });
      
      const { data, error } = await supabase
        .from('os_article_authors')
        .insert({ ...insertData, slug, contact_id: contactId })
        .select()
        .single();
      
      if (error) throw error;
      return parseAuthorData(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['article-authors', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['article-experts', data.brand_id] });
      toast.success('Author created');
    },
    onError: (error) => {
      console.error('Error creating author:', error);
      toast.error('Failed to create author');
    }
  });
};

export const useUpdateArticleAuthor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ArticleAuthor> & { id: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { created_at, updated_at, brand_id, ...updateData } = updates as any;
      
      // Get current author to get brand_id for contact sync
      const { data: currentAuthor } = await supabase
        .from('os_article_authors')
        .select('brand_id, name, email')
        .eq('id', id)
        .single();
      
      if (currentAuthor) {
        // Sync to contacts
        const contactId = await syncAuthorToContacts(currentAuthor.brand_id, {
          name: updates.name || currentAuthor.name,
          email: updates.email || currentAuthor.email,
          phone: null,
        });
        
        if (contactId) {
          updateData.contact_id = contactId;
        }
      }
      
      const { data, error } = await supabase
        .from('os_article_authors')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return parseAuthorData(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['article-authors', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['article-experts', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['article-author', data.id] });
      toast.success('Author updated');
    },
    onError: (error) => {
      console.error('Error updating author:', error);
      toast.error('Failed to update author');
    }
  });
};

export const useDeleteArticleAuthor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: author } = await supabase
        .from('os_article_authors')
        .select('brand_id')
        .eq('id', id)
        .single();
      
      const { error } = await supabase
        .from('os_article_authors')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { brandId: author?.brand_id };
    },
    onSuccess: ({ brandId }) => {
      if (brandId) {
        queryClient.invalidateQueries({ queryKey: ['article-authors', brandId] });
        queryClient.invalidateQueries({ queryKey: ['article-experts', brandId] });
      }
      toast.success('Author deleted');
    },
    onError: (error) => {
      console.error('Error deleting author:', error);
      toast.error('Failed to delete author');
    }
  });
};
